import React, { useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { SheetCanvas } from "./sheet-canvas.tsx";
import { t, type Lang } from "./i18n.ts";
import type { UsedSheet } from "./types.ts";

interface Props {
  sheets: UsedSheet[];
  index: number;
  lang: Lang;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export function KioskView({ sheets, index, lang, onClose, onNavigate }: Props) {
  const sheet = sheets[index]!;
  const hasPrev = index > 0;
  const hasNext = index < sheets.length - 1;

  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && hasPrev) onNavigate(index - 1);
      else if (e.key === "ArrowRight" && hasNext) onNavigate(index + 1);
    },
    [onClose, onNavigate, index, hasPrev, hasNext],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  return (
    <div className="fixed inset-0 z-50 bg-background flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-card shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-lg font-bold">
            {t("sheet_nr", lang)} {index + 1} / {sheets.length}
          </span>
          {sheet.label && <Badge variant="secondary">{sheet.label}</Badge>}
          <Badge
            variant="outline"
            className={
              sheet.waste_percent < 15
                ? "text-green-700 border-green-300"
                : sheet.waste_percent < 30
                  ? "text-yellow-700 border-yellow-300"
                  : "text-red-700 border-red-300"
            }
          >
            {sheet.waste_percent.toFixed(1)}% {t("waste", lang)}
          </Badge>
          <span className="text-muted-foreground">
            {sheet.width}×{sheet.height} mm
          </span>
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="h-5 w-5" />
        </Button>
      </div>

      {/* Canvas area */}
      <div className="flex-1 flex items-center justify-center p-6 min-h-0">
        <div className="w-full h-full flex items-center justify-center">
          <SheetCanvas key={index} sheet={sheet} lang={lang} fullscreen />
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-center gap-4 px-6 py-3 border-t bg-card shrink-0">
        <Button
          variant="outline"
          size="lg"
          onClick={() => onNavigate(index - 1)}
          disabled={!hasPrev}
        >
          <ChevronLeft className="h-5 w-5 mr-1" />
          {hasPrev ? `${t("sheet_nr", lang)} ${index}` : ""}
        </Button>
        <span className="text-sm text-muted-foreground px-4">
          ← → {lang === "pl" ? "nawigacja" : "navigate"} · Esc {lang === "pl" ? "zamknij" : "close"}
        </span>
        <Button
          variant="outline"
          size="lg"
          onClick={() => onNavigate(index + 1)}
          disabled={!hasNext}
        >
          {hasNext ? `${t("sheet_nr", lang)} ${index + 2}` : ""}
          <ChevronRight className="h-5 w-5 ml-1" />
        </Button>
      </div>
    </div>
  );
}
